import type { VenueId, TableId } from "../ids.js";

/** Standort / Betrieb. Beim Zeltfest typischerweise genau einer. */
export interface Venue {
  id: VenueId;
  name: string;
  /** IANA-Zeitzone, z. B. "Europe/Vienna". */
  timezone: string;
  /** ISO-4217, z. B. "EUR". */
  currency: string;
}

/** Tisch oder Ort der Bestellung. Beim Zeltfest evtl. nur Bereich + Nummer oder "Außer Haus". */
export interface Table {
  id: TableId;
  venueId: VenueId;
  /** Anzeige-Label, z. B. "12" oder "Zelt-Links 3". */
  label: string;
  /** Gruppierung, z. B. "Zelt", "Terrasse", "Theke". */
  area: string;
  sortOrder: number;
}

/** Ein zusammenhängender Nummernbereich gültiger Tische (z. B. Zelt: 1–40). */
export interface TableRange {
  /** Bereichsname, z. B. "Zelt" oder "Terrasse" (informativ / Gruppierung). */
  area: string;
  from: number;
  to: number;
}

/**
 * Konfiguration gültiger Tische als Bereiche plus feste Sonderplätze.
 * Reist als Konfig-Snapshot (`ConfigKind "tables"`) offline-first übers Event-Log.
 */
export interface TablesConfig {
  ranges: TableRange[];
  /** Sonderplätze ohne Nummer, z. B. "Außer Haus", "Theke". */
  extras: string[];
}

/** Sind überhaupt Tische konfiguriert? Wenn nein, bleibt die Eingabe frei (wie bisher). */
export function tablesConfigured(cfg: TablesConfig | null | undefined): boolean {
  return !!cfg && ((cfg.ranges?.length ?? 0) > 0 || (cfg.extras?.length ?? 0) > 0);
}

/**
 * Ist ein Tisch-Label gültig? Ohne Konfiguration immer `true` (Freitext-Modus).
 * Sonst: exakter Treffer in `extras` ODER die im Label enthaltene Nummer liegt in einem Bereich.
 */
export function isTableAllowed(cfg: TablesConfig | null | undefined, label: string): boolean {
  if (!tablesConfigured(cfg)) return true;
  if (cfg!.extras?.includes(label)) return true;
  const m = label.match(/\d+/);
  if (!m) return false;
  const n = Number(m[0]);
  return cfg!.ranges.some((r) => n >= r.from && n <= r.to);
}
